/**
 * `<InboxAddressSettings>` — the email-to-inbox address panel (multi-tenancy §6 / M4).
 *
 * Shows the workspace's unguessable `{token}@aletheiapkm.com` address. Mail sent there is
 * ingested as a note in this workspace's Inbox (Plaud transcripts, forwarded notes, anything).
 * The address is a bearer credential, so it can be **copied** and **rotated** (rotate revokes
 * the old one immediately). Reads `GET /api/v1/settings/inbox_address` (minted on first read)
 * and rotates via `POST /api/v1/settings/inbox_address/rotate`.
 */

import { useCallback, useEffect, useState } from 'react';
import { SettingsCard } from './SettingsCard';
import { callEndpoint, getLogger } from '@/api';

const log = getLogger('settings.inbox');

interface InboxAddressData {
  enabled: boolean;
  address?: string;
  domain?: string;
  created_at?: string | null;
}

interface InboxAddressResponse {
  ok: boolean;
  data: InboxAddressData;
}

export function InboxAddressSettings(): JSX.Element {
  const [data, setData] = useState<InboxAddressData | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [rotating, setRotating] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const resp = await callEndpoint<InboxAddressResponse>('GET', 'settings/inbox_address');
        if (!cancelled) setData(resp.data);
      } catch (err) {
        if (!cancelled) setLoadError(String(err));
        log.warn('inbox_address_load_failed', { error: String(err) });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onCopy = useCallback(async () => {
    if (!data?.address) return;
    try {
      await navigator.clipboard.writeText(data.address);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      log.warn('inbox_address_copy_failed', { error: String(err) });
    }
  }, [data?.address]);

  const onRotate = useCallback(async () => {
    if (rotating) return;
    if (
      !window.confirm(
        'Rotate your inbox address? The current address will stop working immediately and ' +
          'anything still configured to send to it (e.g. a Plaud auto-forward) must be updated.',
      )
    ) {
      return;
    }
    setRotating(true);
    try {
      const resp = await callEndpoint<InboxAddressResponse>(
        'POST',
        'settings/inbox_address/rotate',
      );
      setData(resp.data);
    } catch (err) {
      log.warn('inbox_address_rotate_failed', { error: String(err) });
      setLoadError(String(err));
    } finally {
      setRotating(false);
    }
  }, [rotating]);

  return (
    <SettingsCard
      title="Email to inbox"
      desc="Send mail (Plaud transcripts, forwarded notes, anything) to this address and it lands in your Inbox notebook."
    >
      {/* U4: inline styles → settings.css; the redundant inner .s-card-body
          wrappers are gone (SettingsCard already renders one). */}
      {loadError !== null ? (
        <p className="settings-hint settings-hint--error">Couldn't load your inbox address.</p>
      ) : data === null ? (
        <p className="settings-hint">Loading…</p>
      ) : !data.enabled ? (
        <p className="settings-hint">Email to inbox isn't enabled on this deployment yet.</p>
      ) : (
        <div className="settings-stack settings-stack--tight">
          <div className="settings-controls">
            <code className="settings-code">{data.address}</code>
            <button type="button" className="btn btn--sm" onClick={() => void onCopy()}>
              {copied ? 'Copied ✓' : 'Copy'}
            </button>
            <button
              type="button"
              className="btn btn--sm"
              onClick={() => void onRotate()}
              disabled={rotating}
              title="Revoke this address and generate a new one"
            >
              {rotating ? 'Rotating…' : 'Rotate'}
            </button>
          </div>
          <p className="settings-hint settings-hint--soft">
            Anyone who knows this address can add notes to your workspace — treat it like a
            password and rotate it if it leaks.
          </p>
        </div>
      )}
    </SettingsCard>
  );
}
